import React, { useState, useRef, useEffect } from 'react';

const LeftPanel = ({
    examTitle,
    sectionTitle,
    timeLeft,
    audioUrl,
    isStrictAudio,
    partLabel,
    questionCountLabel,
    instruction,
    storyText,
    imageUrl,
    sharedOptions,
    colorOptions,
    sharedColor,
    setSharedColor,
    wordBank,
    wordBankExampleAnswers,
    answerBank,
    answerBankExampleAnswers,
    optionBank,
    optionBankExampleAnswers,
    inputQuestions,
}) => {
    const ASSET_BASE_URL = process.env.REACT_APP_MEDIA_PREFIX || 'http://localhost:8002/uploads';
    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [hasStarted, setHasStarted] = useState(false);

    const formatTime = (seconds) => {
        if (seconds === null || seconds === undefined || seconds < 0) return '0:00';
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
    };

    const getSectionEmoji = (title) => {
        if (!title) return '📚';
        const t = title.toUpperCase();
        if (t.includes('LISTEN')) return '🎧';
        if (t.includes('READ')) return '📖';
        if (t.includes('WRIT')) return '✏️';
        if (t.includes('SPEAK')) return '🎤';
        return '⭐';
    };

    const getAudioSrc = (url) => {
        if (!url) return '';
        if (url.startsWith('http://') || url.startsWith('https://')) {
            return url;
        }
        return `${ASSET_BASE_URL}${url}`;
    };

    const handlePlayStrict = () => {
        if (audioRef.current) {
            if (isStrictAudio) {
                if (!hasStarted) {
                    audioRef.current.play();
                    setIsPlaying(true);
                    setHasStarted(true);
                }
            } else {
                if (isPlaying) {
                    audioRef.current.pause();
                    setIsPlaying(false);
                } else {
                    audioRef.current.play();
                    setIsPlaying(true);
                    setHasStarted(true);
                }
            }
        }
    };

    const handleSeek = (e) => {
        if (!isStrictAudio && audioRef.current) {
            const progressBar = e.currentTarget;
            const rect = progressBar.getBoundingClientRect();
            const clickX = e.clientX - rect.left;
            const width = rect.width;
            const percentage = Math.min(Math.max(clickX / width, 0), 1);
            const newTime = percentage * audioRef.current.duration;
            audioRef.current.currentTime = newTime;
            setProgress(percentage * 100);
        }
    };

    const handleTimeUpdate = () => {
        if (audioRef.current) {
            const current = audioRef.current.currentTime;
            const total = audioRef.current.duration;
            if (total > 0) {
                setProgress((current / total) * 100);
            }
        }
    };

    const handleAudioEnded = () => {
        setIsPlaying(false);
        setProgress(100);
    };

    useEffect(() => {
        setHasStarted(false);
        setIsPlaying(false);
        setProgress(0);
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
        }
    }, [audioUrl]);

    const isLowTime = timeLeft !== null && timeLeft !== undefined && timeLeft <= 60;
    const sectionEmoji = getSectionEmoji(sectionTitle);

    return (
        <div className="w-full flex flex-col gap-2 pb-1 min-h-full h-auto">

            {/* Header Card — hidden on desktop (top bar shows this info) */}
            <div className="md:hidden bg-white rounded-xl shadow-sm border border-gray-100 p-2 shrink-0 flex items-center gap-2">
                <div className="w-9 h-9 shrink-0 rounded-xl bg-hupath-scout/10 flex items-center justify-center text-lg">
                    {sectionEmoji}
                </div>
                <div className="flex flex-col flex-1 min-w-0">
                    <h1 className="text-xs font-black text-hp-on-surface leading-snug truncate">{examTitle}</h1>
                    {sectionTitle && (
                        <span className="mt-0.5 text-[9px] font-black text-hupath-scout uppercase tracking-widest bg-hupath-scout/10 px-1.5 py-0.5 rounded-full inline-block w-max">
                            {sectionTitle}
                        </span>
                    )}
                </div>
                {timeLeft !== undefined && timeLeft !== null && (
                    <div className={`flex items-center gap-1 px-2 py-1 rounded-xl font-black text-xs shrink-0 ${isLowTime
                        ? 'bg-red-500 text-white animate-pulse'
                        : 'bg-red-50 text-red-600'
                        }`}>
                        <span className="material-symbols-outlined text-xs">timer</span>
                        <span>{formatTime(timeLeft)}</span>
                    </div>
                )}
            </div>

            {/* 2. Instruction & Content — vertically centered as one group */}
            <div className="flex flex-col justify-center flex-1 h-auto min-h-min gap-4 text-center px-2">

                {/* Part label + question count */}
                {(partLabel || questionCountLabel) && (
                    <div>
                        {partLabel && (
                            <span className="inline-block px-4 py-1.5 rounded-full bg-hupath-scout text-white font-black text-sm uppercase tracking-widest shadow-sm mb-2">
                                {partLabel}
                            </span>
                        )}
                        {questionCountLabel && (
                            <p className="font-black text-hupath-scout/60 text-lg">{questionCountLabel}</p>
                        )}
                    </div>
                )}

                {/* Instructions */}
                {storyText && (
                    <p className="font-bold text-hp-on-surface text-base leading-relaxed">
                        {storyText}
                    </p>
                )}
                {instruction && (
                    <p className="font-semibold text-hp-on-surface/80 text-sm leading-snug bg-hp-surface p-3 rounded-xl">
                        {instruction}
                    </p>
                )}

                {/* Audio Player — inline with content, not pinned to bottom */}
                {audioUrl && (
                    <div className="flex flex-col gap-2 bg-white border border-gray-200 p-3 rounded-xl shadow-sm">
                        <audio
                            ref={audioRef}
                            src={getAudioSrc(audioUrl)}
                            preload="auto"
                            onTimeUpdate={handleTimeUpdate}
                            onEnded={handleAudioEnded}
                        />
                        <div className="flex items-center gap-3">
                            <button
                                onClick={handlePlayStrict}
                                disabled={isStrictAudio && hasStarted}
                                className={`w-11 h-11 shrink-0 rounded-full flex items-center justify-center text-white transition-all shadow-sm font-bold ${isStrictAudio && hasStarted
                                    ? 'bg-gray-300 cursor-not-allowed'
                                    : 'bg-hupath-scout hover:bg-hupath-forest hover:scale-105'
                                    }`}
                            >
                                <span className="material-symbols-outlined text-2xl">
                                    {isStrictAudio ? (hasStarted ? 'volume_up' : 'play_arrow') : (isPlaying ? 'pause' : 'play_arrow')}
                                </span>
                            </button>
                            <div className="flex-1 min-w-0">
                                <p className="text-[10px] font-bold text-hupath-scout uppercase tracking-widest mb-1 font-label">
                                    {isPlaying ? 'Playing...' : 'Audio Track'}
                                </p>
                                <div
                                    className={`w-full h-2 bg-gray-200 rounded-full overflow-hidden ${!isStrictAudio ? 'cursor-pointer' : ''}`}
                                    onClick={handleSeek}
                                >
                                    <div
                                        className="h-full bg-hupath-scout transition-all duration-300 ease-linear rounded-full"
                                        style={{ width: `${progress}%` }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                    {(imageUrl || (sharedOptions && sharedOptions.length > 0) || (colorOptions && colorOptions.length > 0) || (wordBank && wordBank.length > 0) || (answerBank && Object.keys(answerBank).length > 0) || (optionBank && optionBank.length > 0)) && (
                        <div className="mt-2 flex flex-col items-center gap-2">
                            {imageUrl && (
                                <div className="w-full shrink-0 flex justify-center mb-4">
                                    <img
                                        src={`${ASSET_BASE_URL}${imageUrl}`}
                                        alt="Exercise illustration"
                                        className="rounded-xl w-auto max-w-full max-h-40 object-contain shadow-sm border border-gray-100 p-1 bg-white"
                                    />
                                </div>
                            )}
                            {sharedOptions && sharedOptions.length > 0 && (
                                <div className={`flex-1 min-h-0 grid gap-1.5 w-full content-center ${sharedOptions.length > 6 ? 'grid-cols-4' : 'grid-cols-2 lg:grid-cols-3'}`}>
                                    {sharedOptions.map(option => (
                                        <div key={option.id} className="flex flex-col items-center gap-1 p-1.5 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-amber-200 dark:border-slate-700 min-h-0">
                                            <img src={`${ASSET_BASE_URL}${option.image_url}`} alt={`Option ${option.id}`} className="min-h-0 flex-1 w-full object-contain rounded-md" />
                                            <span className="shrink-0 font-display font-bold text-xs text-amber-600 bg-amber-50 dark:text-amber-400 dark:bg-amber-900/30 w-5 h-5 rounded-full flex items-center justify-center border border-amber-200">{option.id}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {/* Word Bank — inside content card like image */}
                            {wordBank && wordBank.length > 0 && (
                                <div className="mt-1 p-3 rounded-2xl border-2 border-blue-100 dark:border-slate-600" style={{ background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 60%, #eff6ff 100%)' }}>
                                    <div className="flex items-center gap-1.5 mb-2">
                                        <span className="text-base">📖</span>
                                        <p className="font-black text-blue-700 dark:text-blue-300 text-xs uppercase tracking-widest">Word Bank</p>
                                    </div>
                                    <div className="grid grid-cols-3 gap-2">
                                        {wordBank.map(word => {
                                            const isExample = wordBankExampleAnswers && wordBankExampleAnswers.includes(word);
                                            return (
                                                <div
                                                    key={word}
                                                    className={`px-1 py-1.5 rounded-xl text-center flex items-center justify-center text-xs font-bold border-2 transition-all ${isExample
                                                        ? 'bg-blue-200/50 text-blue-400 border-blue-200 line-through opacity-50'
                                                        : 'bg-white text-blue-800 border-blue-200 shadow-sm'
                                                        }`}
                                                >
                                                    {word}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                            {colorOptions && colorOptions.length > 0 && (
                                <div className="mt-4 p-4 bg-white dark:bg-slate-800 rounded-2xl border-2 border-amber-200 dark:border-slate-600 shadow-sm">
                                    <p className="font-bold text-amber-700 dark:text-amber-300 text-sm mb-3 uppercase tracking-wider text-center">Bảng Chọn Màu</p>
                                    <div className="flex justify-center flex-wrap gap-3">
                                        {colorOptions.map(color => (
                                            <div
                                                key={color}
                                                className={`w-10 h-10 rounded-full cursor-pointer border-4 border-white dark:border-gray-800 shadow-sm transition-all hover:-translate-y-1 hover:shadow-md ${sharedColor === color ? 'ring-4 ring-primary-blue scale-110 shadow-lg' : 'hover:scale-105'}`}
                                                style={{ backgroundColor: color.toLowerCase() }}
                                                onClick={() => setSharedColor && setSharedColor(color)}
                                                title={color}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                            {inputQuestions && inputQuestions.length > 0 && (
                                <div className="mt-4 p-4 bg-white dark:bg-slate-800 rounded-2xl border-2 border-purple-200 dark:border-slate-600 shadow-sm">
                                    <p className="font-bold text-purple-700 dark:text-purple-300 text-sm mb-1 uppercase tracking-wider text-center">Điền từ</p>
                                    <p className="text-xs text-purple-400 text-center mb-3">Kéo ô này vào hình (tối đa {inputQuestions.length} ô)</p>
                                    <div
                                        draggable
                                        onDragStart={(e) => {
                                            e.dataTransfer.setData('type', 'inputBox');
                                            // Ghost image matches the rendered input box size (160x44)
                                            const ghost = document.createElement('div');
                                            ghost.style.cssText = 'position:fixed;top:-200px;left:0;width:160px;height:44px;background:rgba(243,232,255,0.95);border:2px dashed #a855f7;border-radius:6px;display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:bold;color:#7e22ce;';
                                            ghost.textContent = 'gõ từ...';
                                            document.body.appendChild(ghost);
                                            e.dataTransfer.setDragImage(ghost, 80, 22);
                                            setTimeout(() => document.body.removeChild(ghost), 0);
                                        }}
                                        className="flex items-center justify-center gap-2 px-4 py-3 bg-purple-50 dark:bg-purple-900/30 border-2 border-dashed border-purple-400 dark:border-purple-500 rounded-xl cursor-grab active:cursor-grabbing hover:shadow-md hover:-translate-y-0.5 hover:bg-purple-100 transition-all select-none"
                                    >
                                        <span className="material-symbols-outlined text-purple-500 text-lg">drag_indicator</span>
                                        <span className="text-sm font-bold text-purple-700 dark:text-purple-300">Kéo vào hình</span>
                                        <span className="material-symbols-outlined text-purple-400 text-base">text_fields</span>
                                    </div>
                                </div>
                            )}
                            {/* Answer Choices — for Reading Part 2 */}
                            {answerBank && Object.keys(answerBank).length > 0 && (
                                <div className="mt-1 p-3 rounded-2xl border-2 border-blue-100 dark:border-slate-600" style={{ background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 60%, #eff6ff 100%)' }}>
                                    <div className="flex items-center gap-1.5 mb-2">
                                        <span className="material-symbols-outlined text-blue-600 text-base">grid_view</span>
                                        <p className="font-black text-blue-700 dark:text-blue-300 text-xs uppercase tracking-widest">Answer Choices</p>
                                    </div>
                                    <div className="space-y-1.5">
                                        {Object.entries(answerBank).map(([letter, text]) => {
                                            const isExample = answerBankExampleAnswers && answerBankExampleAnswers.includes(letter);
                                            return (
                                                <div key={letter} className={`flex items-start gap-2 p-1.5 rounded-xl ${isExample ? 'bg-blue-200/40' : 'bg-white/70'}`}>
                                                    <span className="shrink-0 font-black text-blue-700 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/50 w-5 h-5 rounded-full flex items-center justify-center text-xs border border-blue-200">{letter}</span>
                                                    <p className="text-xs text-slate-700 dark:text-slate-300 m-0 leading-relaxed">
                                                        {text.replace(/\(Example\)/gi, '').trim()}
                                                        {isExample && <span className="font-bold text-blue-500 text-[9px] ml-1 uppercase">(Example)</span>}
                                                    </p>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                            {/* Option Bank — for Reading Part 4 */}
                            {optionBank && optionBank.length > 0 && (
                                <div className="mt-1 p-3 rounded-2xl border-2 border-blue-100 dark:border-slate-600 bg-white dark:bg-card-dark relative overflow-hidden">
                                    <div className="absolute top-0 left-0 w-2 h-full bg-primary-blue"></div>
                                    <div className="mb-2">
                                        <h3 className="exercise-heading-text !text-left flex items-center gap-2 !text-sm !pb-1">
                                            <span className="material-symbols-outlined text-primary-blue !text-base">format_list_bulleted</span>
                                            Choose the correct words
                                        </h3>
                                    </div>
                                    <div className="overflow-x-auto custom-scrollbar">
                                        <table className="w-full text-center border-collapse text-xs">
                                            <thead>
                                                <tr>
                                                    <th className="p-2 text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider border-b-2 border-slate-100 dark:border-slate-800">No.</th>
                                                    <th className="p-2 text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider border-b-2 border-slate-100 dark:border-slate-800">Option A</th>
                                                    <th className="p-2 text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider border-b-2 border-slate-100 dark:border-slate-800">Option B</th>
                                                    <th className="p-2 text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider border-b-2 border-slate-100 dark:border-slate-800">Option C</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {optionBank.map(item => (
                                                    <tr key={item.no} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                                                        <td className="p-2 font-black text-primary-blue bg-blue-50/30 dark:bg-blue-900/10 border-b border-slate-100 dark:border-slate-800">{item.no === 0 ? 'Ex' : item.no}</td>
                                                        {item.options.map((option, index) => {
                                                            const isExampleAnswer = item.no === 0 && optionBankExampleAnswers && optionBankExampleAnswers.includes(option);
                                                            return (
                                                                <td key={index} className="p-2 border-b border-slate-100 dark:border-slate-800">
                                                                    <div className={`exercise-word-bank-item inline-block font-bold !p-1.5 !text-xs ${isExampleAnswer ? 'exercise-word-bank-item-used' : 'exercise-word-bank-item-base'}`}>
                                                                        {option}
                                                                    </div>
                                                                </td>
                                                            );
                                                        })}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                </div>
        </div>
    );
};

export default LeftPanel;
